import type { ElMessageBoxOptions, MessageBoxData } from 'element-plus';
import { ElMessageBox } from 'element-plus';
import type { App } from 'vue';

export interface FDMessageBoxInstance {
  (message: string, title: string, options?: ElMessageBoxOptions): Promise<MessageBoxData>;
  confirm: (
    message: string,
    title: string,
    options?: ElMessageBoxOptions
  ) => Promise<MessageBoxData>;
  alert: (message: string, title: string, options?: ElMessageBoxOptions) => Promise<MessageBoxData>;
  prompt: (
    message: string,
    title: string,
    options?: ElMessageBoxOptions
  ) => Promise<MessageBoxData>;
}

const DEFAULT_CLASS = 'fd-message-box';

/**
 * 合併預設選項，自動套用 fd-message-box customClass
 * @param options - 選項參數
 */
const normalizeOptions = (options: ElMessageBoxOptions = {}): ElMessageBoxOptions => ({
  center: true,
  showClose: false,
  ...options,
  customClass: options.customClass ? `${DEFAULT_CLASS} ${options.customClass}` : DEFAULT_CLASS
});

/**
 * FDMessageBox 實例
 * @param message - 訊息內容
 * @param title - 訊息標題
 * @param options - 選項參數
 */
const FDMessageBox: FDMessageBoxInstance = ((
  message: string,
  title: string,
  options?: ElMessageBoxOptions
): Promise<MessageBoxData> =>
  ElMessageBox({ message, title, ...normalizeOptions(options) })) as FDMessageBoxInstance;

/**
 * 建立各訊息類型方法
 */
FDMessageBox.confirm = (message, title, options) =>
  ElMessageBox.confirm(message, title, normalizeOptions(options));

FDMessageBox.alert = (message, title, options) =>
  ElMessageBox.alert(message, title, normalizeOptions(options));

FDMessageBox.prompt = (message, title, options) =>
  ElMessageBox.prompt(message, title, normalizeOptions(options));

/**
 * 將 FDMessageBox 註冊為 Vue 插件
 */
export const FDMessageBoxPlugin = {
  install(app: App) {
    app.config.globalProperties.$fdMessageBox = FDMessageBox;
  }
};

export default FDMessageBox;
